import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { usePreview } from '../PreviewContext';
import { useSearchTerm } from '../SearchContext';
import TextMutator from './TextMutator';
import NumberMutator from './NumberMutator';
import SwitchMutator from './SwitchMutator';
import { isYArray, isYMap } from '../../hooks/documentHooks';
import ListMutator from './ListMutator';
import ObjectMutator from './ObjectMutator';
import { Tooltip } from '@mui/material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ImageMutator from './ImageMutator';
import InvalidTypeMutator from './InvalidTypeMutator';
import { AssistantPhoto } from '@mui/icons-material';
import ParagraphMutator from './ParagraphMutator';

const CONTROLS = {
  'text': TextMutator,
  'number': NumberMutator,
  'switch': SwitchMutator,
  'paragraph': ParagraphMutator, 
  'image': ImageMutator,
  'list': ListMutator,
  'object': ObjectMutator
}

const CustomElement = styled.div`
  display: flex;
  width: 100%;
  ${({ highlight }) => highlight && `
    background: #ffffbc;
    border-radius: 5px;
  `}
`

const ElementRenderer = ({ definition, value, parent, itemKey, path = '', parentDefinition, dataTranslation, dataTranslationMap }) => {
  const translatable = dataTranslation
  const translation = useMemo(() => {
    if (translatable) {
      return dataTranslation && dataTranslation[path]
    }
  }, [path, translatable, dataTranslation])
  const hasTranslation = translatable && (translation !== null && translation !== undefined)
  const finalValue = useMemo(() => {
    return (translatable && hasTranslation) ? translation : value
  }, [translatable, translation, hasTranslation, value])

  const [originalValue, setOriginalValue] = useState(finalValue)
  const [highlight, setHighlight] = useState(false)
  const { foundItems } = useSearchTerm()
  const { hotReloadPage } = usePreview()

  const Element = useMemo(() => {
    if (definition?.control) {
      const control = CONTROLS[definition.control]
      if (!control) return InvalidTypeMutator
      return control
    } else {
      if (typeof finalValue === 'string') return TextMutator
      if (typeof finalValue === 'number') return NumberMutator
      if (typeof finalValue === 'boolean') return SwitchMutator
      if (isYArray(finalValue)) return ListMutator
      if (isYMap(finalValue)) return ObjectMutator
      return InvalidTypeMutator
    }
  }, [definition, finalValue])

  const Buttons = useMemo(() => {
    return (
      <>
        {translatable && !hasTranslation && (
          <Tooltip title="Esta entrada no traducción.">
            <AssistantPhoto color="error" />
          </Tooltip>    
        )}
        {!definition &&
          <Tooltip title="Esta entrada no tiene definición.">
            <ReportGmailerrorredIcon color="error" />
          </Tooltip>    
        }
      </>
    )
  }, [definition, translatable, hasTranslation])

  const handleUpdate = useCallback((newValue) => {
    if (translatable) {
      dataTranslationMap.set(path, newValue)
    } else {
      parent.set(itemKey, newValue)
    }
  }, [parent, itemKey, path, dataTranslationMap, translatable])

  useEffect(() => {
    if (foundItems) {
      const found = Object.keys(foundItems).find(item => item.startsWith(path))
      setHighlight(found === path)
    }
  }, [foundItems, path])

  useEffect(() => {
    if (originalValue !== finalValue) {
      const timer = setTimeout(() => {
        setOriginalValue(finalValue)
        hotReloadPage()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [finalValue, originalValue, hotReloadPage])

  return (
    <CustomElement highlight={highlight}>
      <Element 
        definition={definition}
        value={finalValue}
        path={path}
        parent={parent}
        itemKey={itemKey}
        buttons={Buttons}
        parentDefinition={parentDefinition}
        dataTranslation={dataTranslation}
        dataTranslationMap={dataTranslationMap}
        handleUpdate={handleUpdate}
      />
    </CustomElement>
  )
}

export default ElementRenderer;